import { useEffect, useState } from "react";
import { Check, CheckCircle2, MessageCircle, RotateCcw, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eyebrow, Reveal } from "./motion";
import MapPicker from "./MapPicker";
import api, { formatApiError } from "@/lib/api";
import { mapsPointLink } from "@/lib/constants";
import { useToast } from "@/hooks/use-toast";

const IMG_DELIVERY = "/kurir-illustration.webp";

const BENEFITS = [
    "Kurir datang sesuai jadwal yang Anda pilih",
    "Tempel titik lokasi agar kurir tepat sampai",
    "Konfirmasi & update status via WhatsApp",
];

const initialForm = {
    name: "",
    phone: "",
    address: "",
    preferred_date: "",
    pickup_time: "",
    notes: "",
};

export default function PickupForm() {
    const [form, setForm] = useState(initialForm);
    const [point, setPoint] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const { toast } = useToast();

    const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim(),
                address: form.address.trim(),
                preferred_date: form.preferred_date || null,
                pickup_time: form.pickup_time || null,
                pickup_lat: point ? point.lat : null,
                pickup_lng: point ? point.lng : null,
                notes: form.notes.trim() ? form.notes.trim() : null,
            };
            const res = await api.post("/orders", payload);
            setResult(res.data);
            toast({
                title: "Permintaan terkirim!",
                description: "Konfirmasi jadwal lewat WhatsApp agar lebih cepat.",
            });
        } catch (err) {
            toast({
                title: "Gagal mengirim permintaan",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setForm(initialForm);
        setPoint(null);
        setResult(null);
    };

    return (
        <section id="form" className="relative py-24 lg:py-32">
            <div className="pointer-events-none absolute -left-40 top-40 h-[400px] w-[400px] rounded-full bg-brand-mist/70 blur-3xl" />

            <div className="relative mx-auto grid max-w-7xl items-start gap-14 px-6 lg:grid-cols-2">
                {/* Left — pitch + image */}
                <div>
                    <Reveal>
                        <Eyebrow>Antar Jemput</Eyebrow>
                        <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
                            Jadwalkan penjemputan sekarang.
                        </h2>
                        <p className="mt-5 max-w-lg text-base leading-relaxed text-ink/60 sm:text-lg">
                            Isi form di samping, tandai titik lokasi Anda di
                            peta, dan kurir DigiKlin akan menuju lokasi. Setelah
                            terkirim, lanjutkan konfirmasinya lewat WhatsApp.
                        </p>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <ul className="mt-8 space-y-3.5">
                            {BENEFITS.map((b) => (
                                <li
                                    key={b}
                                    className="flex items-start gap-3 text-sm font-semibold text-ink/70"
                                >
                                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-mist text-brand">
                                        <Check size={13} strokeWidth={3} />
                                    </span>
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal delay={0.25}>
                        <div className="mt-10 overflow-hidden rounded-3xl shadow-brand-lg">
                            <img
                                src={IMG_DELIVERY}
                                alt="Ilustrasi kurir DigiKlin mengantar jemput cucian dengan van DigiKlin"
                                className="h-64 w-full object-cover sm:h-80"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                </div>

                {/* Right — form / success */}
                <Reveal delay={0.1}>
                    <div
                        data-testid="pickup-form-card"
                        className="rounded-3xl border border-border bg-white p-8 shadow-brand-lg sm:p-10"
                    >
                        {result ? (
                            <div
                                data-testid="pickup-form-success"
                                className="text-center"
                            >
                                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand-mist text-brand">
                                    <CheckCircle2 size={32} />
                                </span>
                                <h3 className="mt-6 font-display text-2xl font-extrabold text-ink">
                                    Permintaan terkirim!
                                </h3>
                                <p className="mt-2 text-sm text-ink/60">
                                    Kode order Anda:
                                </p>
                                <p
                                    data-testid="pickup-order-code"
                                    className="mt-2 inline-block rounded-full bg-brand-mist px-6 py-2 font-display text-xl font-extrabold tracking-widest text-brand"
                                >
                                    {result.order.order_code}
                                </p>
                                <p className="mx-auto mt-5 max-w-sm text-sm leading-relaxed text-ink/60">
                                    Simpan kode ini. Langkah terakhir —
                                    konfirmasi jadwal penjemputan Anda lewat
                                    WhatsApp agar kurir segera menuju lokasi.
                                </p>
                                {result.order.pickup_lat != null && (
                                    <a
                                        data-testid="success-maps-link"
                                        href={mapsPointLink(result.order.pickup_lat, result.order.pickup_lng)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:underline"
                                    >
                                        <MapPin size={13} />
                                        Lihat titik lokasi Anda di Google Maps
                                    </a>
                                )}
                                <div className="mt-8 flex flex-col gap-3">
                                    <Button
                                        data-testid="wa-confirm-button"
                                        asChild
                                        className="h-12 rounded-full bg-[#22c55e] text-base font-bold text-white hover:bg-[#16a34a]"
                                    >
                                        <a
                                            href={result.whatsapp_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <MessageCircle size={18} className="mr-2" />
                                            Konfirmasi via WhatsApp
                                        </a>
                                    </Button>
                                    <Button
                                        data-testid="new-request-button"
                                        variant="outline"
                                        onClick={reset}
                                        className="h-12 rounded-full border-ink/10 text-base font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                    >
                                        <RotateCcw size={16} className="mr-2" />
                                        Buat Permintaan Lain
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <form
                                data-testid="pickup-form"
                                onSubmit={submit}
                                className="space-y-5"
                            >
                                <h3 className="font-display text-2xl font-extrabold text-ink">
                                    Form Antar Jemput
                                </h3>

                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="pf-name" className="text-sm font-bold text-ink/80">
                                            Nama Lengkap
                                        </Label>
                                        <Input
                                            id="pf-name"
                                            data-testid="pickup-input-name"
                                            required
                                            minLength={2}
                                            placeholder="Nama Anda"
                                            value={form.name}
                                            onChange={(e) => set("name")(e.target.value)}
                                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="pf-phone" className="text-sm font-bold text-ink/80">
                                            Nomor WhatsApp
                                        </Label>
                                        <Input
                                            id="pf-phone"
                                            data-testid="pickup-input-phone"
                                            required
                                            type="tel"
                                            inputMode="tel"
                                            placeholder="08xxxxxxxxxx"
                                            value={form.phone}
                                            onChange={(e) => set("phone")(e.target.value)}
                                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="pf-address" className="text-sm font-bold text-ink/80">
                                        Alamat Penjemputan
                                    </Label>
                                    <Textarea
                                        id="pf-address"
                                        data-testid="pickup-input-address"
                                        required
                                        minLength={8}
                                        rows={3}
                                        placeholder="Nama jalan, nomor rumah, patokan..."
                                        value={form.address}
                                        onChange={(e) => set("address")(e.target.value)}
                                        className="rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm font-bold text-ink/80">
                                        Titik Lokasi di Peta{" "}
                                        <span className="font-medium text-ink/40">(opsional — biar kurir tidak nyasar)</span>
                                    </Label>
                                    <MapPicker value={point} onChange={setPoint} />
                                </div>

                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="pf-date" className="text-sm font-bold text-ink/80">
                                            Tanggal Penjemputan{" "}
                                            <span className="font-medium text-ink/40">(opsional)</span>
                                        </Label>
                                        <Input
                                            id="pf-date"
                                            data-testid="pickup-input-date"
                                            type="date"
                                            value={form.preferred_date}
                                            onChange={(e) => set("preferred_date")(e.target.value)}
                                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="pf-time" className="text-sm font-bold text-ink/80">
                                            Jam Penjemputan
                                        </Label>
                                        <Input
                                            id="pf-time"
                                            data-testid="pickup-input-time"
                                            required
                                            type="time"
                                            value={form.pickup_time}
                                            onChange={(e) => set("pickup_time")(e.target.value)}
                                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="pf-notes" className="text-sm font-bold text-ink/80">
                                        Catatan{" "}
                                        <span className="font-medium text-ink/40">(opsional)</span>
                                    </Label>
                                    <Textarea
                                        id="pf-notes"
                                        data-testid="pickup-input-notes"
                                        rows={2}
                                        placeholder="Contoh: ada noda di kerah kemeja putih"
                                        value={form.notes}
                                        onChange={(e) => set("notes")(e.target.value)}
                                        className="rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                    />
                                </div>

                                <Button
                                    data-testid="pickup-form-submit"
                                    type="submit"
                                    disabled={loading}
                                    className="w-full rounded-full bg-brand py-4 text-base font-bold text-white shadow-brand transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-deep hover:shadow-brand-lg disabled:opacity-60"
                                >
                                    {loading ? "Mengirim permintaan..." : "Kirim Permintaan Antar Jemput"}
                                </Button>
                                <p className="text-center text-xs leading-relaxed text-ink/40">
                                    Setelah terkirim, Anda akan diarahkan ke
                                    WhatsApp untuk konfirmasi jadwal.
                                </p>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
